import { spotifyQueries, SpotifyTrackSearchResult } from '@round/api';
import { ComboboxItem } from '@round/creatorbase-ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { UIEventHandler, useCallback, useMemo, useState } from 'react';

const queryOptions = spotifyQueries.trackSearch.options.infinite;

const LIST_SCROLL_THRESHOLD_PX = 80;

export type SpotifyTrackItem = ComboboxItem<string> & Pick<SpotifyTrackSearchResult, 'artists' | 'image_url'>;

export const mapToItem = (track: SpotifyTrackSearchResult): SpotifyTrackItem => ({
    value: track.spotify_id,
    label: track.title,
    artists: track.artists,
    image_url: track.image_url,
});

export function useSpotifyTrackCombobox() {
    const [search, setSearch] = useState('');
    const isSearching = search.length > 0;

    const { data, isFetching, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
        ...queryOptions({ keyword: search, page_size: 10 }),
        enabled: isSearching,
    });

    const items = useMemo(() => data?.pages.flatMap((page) => page.results.map(mapToItem)) ?? [], [data]);

    const onItemsListScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        (event) => {
            const el = event.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight > LIST_SCROLL_THRESHOLD_PX) {
                return;
            }
            if (hasNextPage && !isFetching) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetching]
    );

    return {
        items,
        isFetching,
        isError,
        isSearching,
        /** Disables Base UI client-side filtering; list is driven by API keyword via debounced query. */
        filter: null,
        onInputValueChange: setSearch,
        onItemsListScroll,
    };
}
